import { FC, useCallback, useEffect, useMemo } from 'react'
import { Table, Space, Modal, Pagination } from 'antd'
import TableWrapper from '../../../../../components/table-wrapper/table-wrapper'
import { getColumns, orderLevelCell } from '../../constants/column'
import { OrderStatus } from '../../../../../types/api/form-order'
import useEndpointQueryParams from '../../../../../hooks/table-pages/use-endpoint-query-parameters'
import useTablePagination from '../../../../../hooks/table-pages/use-table-pagination'
import { useGetFormOrdersQuery } from '../../../../../store/api/form-orders-api'
import { EndpointQueryParameters } from '../../../../../types/strapi/strapi'
import { FormsDataSource } from '../../helpers/forms-data-source'
import { FormsTableRow } from '../../types/forms-table-row'
import Accept from '../accept/accept'

export type FlexoFormsTableTab = 'Ожидаемые' | 'Завершенные'

interface FlexoFormsTableProps {
    tab: FlexoFormsTableTab
}

const canAccept = (status: OrderStatus) =>
    status === 'Готов к выдаче' || status === 'В пути' || status === 'Создан'

const FlexoFormsTable: FC<FlexoFormsTableProps> = ({ tab }) => {
    const baseParams = useEndpointQueryParams({
        deserializeFilters: () => ({}),
    })

    const params: EndpointQueryParameters = useMemo(
        () => ({
            ...baseParams,
            filters: {
                ...(baseParams.filters || {}),
                orderStatus: tab === 'Ожидаемые' ? { $ne: 'Завершен' } : { $eq: 'Завершен' },
            },
            sort: ['estimatedDate:desc'],
            populate: {
                forms: {
                    populate: {
                        form_set: true,
                    },
                },
                shipment: {
                    populate: { designer: true },
                },
            },
        }),
        [baseParams, tab]
    )

    const { data, isLoading, refetch, isError, error } = useGetFormOrdersQuery(params)
    const pagination = useTablePagination(params, data)
    const dataSource = useMemo(() => FormsDataSource(data?.data ?? []), [data])

    const tableFooter = useCallback(
        () => <Pagination {...pagination} size='small' align='end' />,
        [pagination]
    )

    const columnsData = useMemo(() => {
        const baseColumns = getColumns({
            onRefetch: refetch,
            disableStockEdit: tab === 'Завершенные',
        })

        if (tab === 'Ожидаемые') {
            baseColumns.push({
                key: 'actions',
                title: 'Действия',
                fixed: 'right',
                width: 90,
                onCell: orderLevelCell,
                render: (_, record: FormsTableRow) => {
                    if (record.rowSpan === 0) return null
                    const disabled = !canAccept(record.orderStatus) || !record.allFormsProcessed

                    return (
                        <Space>
                            <Accept documentId={record.orderDocumentId} disabled={disabled} />
                        </Space>
                    )
                },
            })
        }
        return baseColumns
    }, [tab, refetch])

    useEffect(() => {
        if (isError) {
            Modal.error({
                title: 'Ошибка загрузки приходов форм',
                content: JSON.stringify(error),
                footer: 'Пожалуйста, обратитесь к администратору. Отправьте скриншот ошибки на почту ryzhakov.r@mail.ru.',
            })
        }
        // Модалка — один раз при переходе в ошибку, а не на каждый новый объект error
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError])

    return (
        <TableWrapper style={{ flex: 'auto' }} filtersSummaryHeight={0}>
            <Table<FormsTableRow>
                bordered
                columns={columnsData}
                dataSource={dataSource}
                pagination={false}
                footer={tableFooter}
                rowKey={record => record.key}
                loading={isLoading}
            />
        </TableWrapper>
    )
}

export default FlexoFormsTable
